/**
 * Trade-in line items — a card per item showing the initiated (quoted) vs received (graded)
 * comparison, final trade-in value, per-item flags, the linked inventory item, and the
 * supplier's own description of the item (`initiatedLikeDescription`).
 *
 * A partner-facing subset of reware-admin's TradeinItem detail block, not a copy: the tag
 * values, value displays, received data blob and per-item history it shows are not selected.
 */
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { displayNameFor, SectionLabel } from '@src/components/indexPage';

import { formatMoney } from './format';
import { present, type ConstantOption, type TradeinItem, type TradeinRecord } from './types';

const quotedRange = (item: TradeinItem, currency: string | null | undefined): string => {
  const { initiatedQuotedValueLow: low, initiatedQuotedValue: high } = item;
  if (low == null && high == null) return '—';
  if (low != null && high != null && low !== high) {
    return `${formatMoney(low, currency)} – ${formatMoney(high, currency)}`;
  }
  return formatMoney(high ?? low, currency);
};

/** Initiated → Received value pair for one attribute. */
const Compare = ({
  label,
  initiated,
  received,
}: {
  label: string;
  initiated: React.ReactNode;
  received: React.ReactNode;
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: 1, py: 0.25 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontSize: 13 }}>{initiated || '—'}</Typography>
      <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>{received || '—'}</Typography>
    </Stack>
  </Box>
);

const TradeinItemCard = ({
  item,
  currency,
  conditions,
  rejectReasons,
}: {
  item: TradeinItem;
  currency: string | null | undefined;
  conditions: ConstantOption[];
  rejectReasons: ConstantOption[];
}) => {
  const catalog = item.catalogItem;
  const inv = item.inventoryItem;
  const image = inv?.imageUrls ? (inv.imageUrls.front ?? Object.values(inv.imageUrls)[0]) : undefined;
  const rejected = item.receivedRejectedReason;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
    >
      <Avatar
        variant="rounded"
        src={image}
        alt={catalog?.title ?? 'Item'}
        sx={{ width: 56, height: 56, bgcolor: 'grey.100', color: 'grey.500', border: '1px solid', borderColor: 'divider' }}
      >
        <CheckroomIcon fontSize="small" />
      </Avatar>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {catalog?.title ?? `Item ${item.id}`}
          </Typography>
          {rejected && (
            <Chip size="small" color="error" label={`Rejected: ${displayNameFor(rejectReasons, rejected)}`} />
          )}
          {item.receivedNeedsRepair && <Chip size="small" color="warning" label="Needs Repair" />}
          {item.receivedNeedsCleaning && <Chip size="small" color="warning" label="Needs Cleaning" />}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {`${catalog?.parent_sku ?? '—'}${inv?.id ? ` · Item #${inv.id}` : ''}${inv?.location?.code ? ` · ${inv.location.code}` : ''}`}
        </Typography>

        <Box sx={{ mt: 0.75 }}>
          <Compare
            label="Condition"
            initiated={item.initiatedCondition}
            received={item.receivedCondition ? displayNameFor(conditions, item.receivedCondition) : null}
          />
          <Compare
            label="Value"
            initiated={quotedRange(item, currency)}
            received={item.tradeinPrice != null ? formatMoney(item.tradeinPrice, currency) : null}
          />
          <Compare
            label="Department"
            initiated={item.initiatedDepartment}
            received={item.receivedDepartment}
          />
          <Compare label="Category" initiated={item.initiatedCategory} received={item.receivedCategory} />
        </Box>

        {item.initiatedLikeDescription && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
            {`Supplier: “${item.initiatedLikeDescription}”`}
          </Typography>
        )}
        {item.receivedNotes && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
            {item.receivedNotes}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export const TradeinItems = ({
  tradein,
  conditions,
  rejectReasons,
}: {
  tradein: TradeinRecord;
  conditions: ConstantOption[];
  rejectReasons: ConstantOption[];
}) => {
  // Filtered, and the heading count comes from the filtered array — a null element would
  // throw inside the map below, and a count off the raw list disagrees with the cards shown.
  const items = present(tradein.tradeinItems);
  if (!items.length) return null;
  return (
    <Box>
      <SectionLabel>{`Items (${items.length})`}</SectionLabel>
      <Stack spacing={1}>
        {items.map(item => (
          <TradeinItemCard
            key={item.id}
            item={item}
            currency={tradein.transactionCurrencyCode}
            conditions={conditions}
            rejectReasons={rejectReasons}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default TradeinItems;
